import {
  AsyncLabelLoaders,
  AsyncSuggestionsLoaders,
  QueryBuilder,
  QueryBuilderProps,
} from '@ardoq/query-builder';
import {
  getLoadComponentSuggestionsAsync,
  getLoadTagSuggestionsAsync,
  getLoadUserSuggestionsAsync,
  getLoadWorkspaceSuggestionsAsync,
  getLoadComponentTypeNameSuggestionsAsync,
  getLoadReferenceTypeNameSuggestionsAsync,
} from '@ardoq/suggestions-api';
import { ArdoqId } from '@ardoq/api-types';
import { isArdoqError } from '@ardoq/common-helpers';
import { componentApi, referenceApi, workspaceApi } from '@ardoq/api';
import { connect } from '@ardoq/rxbeach';
import { map } from 'rxjs';
import type { OrgUsersById } from 'streams/orgUsers/types';
import { queryBuilderLabelSources$ } from './queryBuilderLabelSources$';
import type { QueryBuilderLabelSources } from './types';

type QueryBuilderWithSuggestionsLoadersProps = Omit<
  QueryBuilderProps,
  'asyncSuggestionsLoaders' | 'asyncLabelLoaders'
>;

const getComponentName =
  (componentsById: QueryBuilderLabelSources['componentsById']) =>
  async (id: ArdoqId): Promise<string | undefined> => {
    const componentName = componentsById[id]?.name;
    if (componentName) return componentName;
    const comp = await componentApi.fetch(id);
    if (isArdoqError(comp)) return;
    return comp.name || comp._id || 'Name not found';
  };

const getReferenceName =
  (referencesById: QueryBuilderLabelSources['referencesById']) =>
  async (id: string): Promise<string | undefined> => {
    const referenceName = referencesById[id]?.displayText;
    if (referenceName) return referenceName;
    const ref = await referenceApi.fetch(id);
    if (isArdoqError(ref)) return;
    return ref.displayText || ref._id || 'Name not found';
  };

const getWorkspaceName =
  (workspacesById: QueryBuilderLabelSources['workspacesById']) =>
  async (id: ArdoqId): Promise<string | undefined> => {
    const workspaceName = workspacesById[id]?.name;
    if (workspaceName) return workspaceName;
    const ws = await workspaceApi.fetch(id);
    if (isArdoqError(ws)) return;
    return ws.name || ws._id || 'Name not found';
  };

const getUserName =
  (orgUsersById: OrgUsersById) =>
  async (id: ArdoqId): Promise<string | undefined> => {
    const user = orgUsersById[id];
    if (!user) return;
    return `${user.name} (${user.email})`;
  };

type GetQueryBuilderLoadersArgs = {
  labelSources: QueryBuilderLabelSources;
  workspaceIds?: ArdoqId[];
};

export const getQueryBuilderLoaders = ({
  labelSources: {
    orgUsersById,
    componentsById,
    referencesById,
    workspacesById,
  },
  workspaceIds,
}: GetQueryBuilderLoadersArgs): {
  asyncSuggestionsLoaders: AsyncSuggestionsLoaders;
  asyncLabelLoaders: AsyncLabelLoaders;
} => ({
  asyncSuggestionsLoaders: {
    loadReferenceOIDSuggestionsAsync: async () => [], // we don't have proper suggestion endpoint to fetch data
    loadComponentKeySuggestionsAsync: async () => [], // we don't have proper suggestion endpoint to fetch data
    loadComponentSuggestionsIncludingCurrentComponentAsync:
      getLoadComponentSuggestionsAsync({ workspaceIds }),
    loadParentComponentSuggestionsAsync: getLoadComponentSuggestionsAsync({
      workspaceIds,
    }),
    loadComponentSuggestionsAsync: getLoadComponentSuggestionsAsync({
      workspaceIds,
    }),
    loadWorkspaceSuggestionsAsync: getLoadWorkspaceSuggestionsAsync({
      workspaceIds,
    }),
    loadUserSuggestionsAsync: getLoadUserSuggestionsAsync({
      workspaceIds,
    }),
    loadTagSuggestionsAsync: getLoadTagSuggestionsAsync({
      workspaceIds,
    }),
    loadComponentTypeNameSuggestionsAsync:
      getLoadComponentTypeNameSuggestionsAsync({ workspaceIds }),
    loadReferenceTypeNameSuggestionsAsync:
      getLoadReferenceTypeNameSuggestionsAsync({ workspaceIds }),
  },
  asyncLabelLoaders: {
    getComponentKeyLabelAsync: async key => key,
    getComponentLabelAsync: getComponentName(componentsById),
    getWorkspaceLabelAsync: getWorkspaceName(workspacesById),

    getUserLabelAsync: getUserName(orgUsersById),
    getReferenceLabelAsync: getReferenceName(referencesById),
  },
});

const QueryBuilderWithSuggestionsLoadersComponent = ({
  labelSources,
  ...props
}: QueryBuilderWithSuggestionsLoadersProps & {
  labelSources: QueryBuilderLabelSources;
}) => (
  <QueryBuilder
    {...props}
    {...getQueryBuilderLoaders({
      labelSources,
      workspaceIds: props.contextWorkspaceIds,
    })}
  />
);

const labelSourcesProps$ = queryBuilderLabelSources$.pipe(
  map(labelSources => ({ labelSources }))
);

export const QueryBuilderWithSuggestionsLoaders = connect(
  QueryBuilderWithSuggestionsLoadersComponent,
  labelSourcesProps$
);
